import React from 'react'
import {
    Grid,
    TextField,
    IconButton,
    InputAdornment,
    Typography,
    Button,
} from '@mui/material'
import { Add as AddIconSB } from '@material-ui/icons'
import { Delete as DeleteIconSB } from '@material-ui/icons'

import { SectionTitle } from './styles'
import { FormUser } from '../../models/Forms'

export interface FormUsersValue {
    regional_manager: FormUser
    property_manager?: FormUser
    other_users?: FormUser[]
}

interface Props {
    value: FormUsersValue
    showValidation: boolean
    variant?: 'default' | 'wizard'
    onManagerChange: (
        managerType: 'regional_manager' | 'property_manager',
        field: 'name' | 'email',
        value: string,
    ) => void
    onOtherUserChange: (
        index: number,
        field: 'name' | 'email',
        value: string,
    ) => void
    onAddOtherUser: () => void
    onRemoveOtherUser: (index: number) => void
}

export const isUserFieldIncomplete = (
    name: string | undefined,
    email: string | undefined,
    showValidation: boolean,
): boolean => {
    return Boolean(showValidation && ((name && !email) || (!name && email)))
}

export const hasIncompleteFormUsers = (value: FormUsersValue): boolean => {
    const hasIncompletePropertyManager =
        (value.property_manager?.name && !value.property_manager?.email) ||
        (!value.property_manager?.name && value.property_manager?.email)

    const hasIncompleteOtherUsers = value.other_users?.some(
        (user) => (user.name && !user.email) || (!user.name && user.email),
    )

    const hasIncompleteRegionalManager =
        !value.regional_manager?.name || !value.regional_manager?.email

    return Boolean(
        hasIncompleteRegionalManager ||
            hasIncompletePropertyManager ||
            hasIncompleteOtherUsers,
    )
}

export const FormUsersSection = ({
    value,
    showValidation,
    variant = 'default',
    onManagerChange,
    onOtherUserChange,
    onAddOtherUser,
    onRemoveOtherUser,
}: Props) => {
    const incomplete = (name: string | undefined, email: string | undefined) =>
        isUserFieldIncomplete(name, email, showValidation)

    const isWizard = variant === 'wizard'
    const fieldSize = isWizard ? 'small' : 'medium'

    const heading = (label: string, hint?: string) =>
        isWizard ? (
            <Grid item xs={12}>
                <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginTop: 4 }}
                >
                    {label}
                </Typography>
                {hint && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        style={{ marginBottom: 8 }}
                    >
                        {hint}
                    </Typography>
                )}
            </Grid>
        ) : (
            <Grid item xs={12}>
                <SectionTitle variant="h6">{label}</SectionTitle>
            </Grid>
        )

    return (
        <>
            {heading(
                'Regional Manager',
                isWizard
                    ? 'Required. This person will be invited to every selected property.'
                    : undefined,
            )}
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size={fieldSize}
                    label="Name"
                    value={value.regional_manager.name}
                    onChange={(e) =>
                        onManagerChange(
                            'regional_manager',
                            'name',
                            e.target.value,
                        )
                    }
                    required
                    error={showValidation && !value.regional_manager.name}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size={fieldSize}
                    type="email"
                    label="Email"
                    value={value.regional_manager.email}
                    onChange={(e) =>
                        onManagerChange(
                            'regional_manager',
                            'email',
                            e.target.value,
                        )
                    }
                    required
                    error={showValidation && !value.regional_manager.email}
                />
            </Grid>

            {heading(
                'Property Manager',
                isWizard
                    ? 'Optional. Assigned as the property manager on each new property.'
                    : undefined,
            )}
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size={fieldSize}
                    label="Name"
                    value={value.property_manager?.name || ''}
                    onChange={(e) =>
                        onManagerChange(
                            'property_manager',
                            'name',
                            e.target.value,
                        )
                    }
                    error={incomplete(
                        value.property_manager?.name,
                        value.property_manager?.email,
                    )}
                    helperText={
                        incomplete(
                            value.property_manager?.name,
                            value.property_manager?.email,
                        )
                            ? 'Both name and email are required'
                            : ''
                    }
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size={fieldSize}
                    type="email"
                    label="Email"
                    value={value.property_manager?.email || ''}
                    onChange={(e) =>
                        onManagerChange(
                            'property_manager',
                            'email',
                            e.target.value,
                        )
                    }
                    error={incomplete(
                        value.property_manager?.name,
                        value.property_manager?.email,
                    )}
                    helperText={
                        incomplete(
                            value.property_manager?.name,
                            value.property_manager?.email,
                        )
                            ? 'Both name and email are required'
                            : ''
                    }
                />
            </Grid>

            {isWizard ? (
                <Grid
                    item
                    xs={12}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: 8,
                    }}
                >
                    <div>
                        <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 600 }}
                        >
                            Other Users
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Optional. Each person is invited to every selected
                            property.
                        </Typography>
                    </div>
                    <Button
                        size="small"
                        color="primary"
                        startIcon={<AddIconSB />}
                        onClick={onAddOtherUser}
                        style={{ textTransform: 'none' }}
                    >
                        Add user
                    </Button>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <SectionTitle variant="h6">
                        Other Users
                        <IconButton color="primary" onClick={onAddOtherUser}>
                            <AddIconSB />
                        </IconButton>
                    </SectionTitle>
                </Grid>
            )}
            {value.other_users?.map((user, index) => (
                <React.Fragment key={index}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size={fieldSize}
                            label="Name"
                            value={user.name}
                            onChange={(e) =>
                                onOtherUserChange(index, 'name', e.target.value)
                            }
                            error={incomplete(user.name, user.email)}
                            helperText={
                                incomplete(user.name, user.email)
                                    ? 'Both name and email are required'
                                    : ''
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size={fieldSize}
                            type="email"
                            label="Email"
                            value={user.email}
                            onChange={(e) =>
                                onOtherUserChange(
                                    index,
                                    'email',
                                    e.target.value,
                                )
                            }
                            error={incomplete(user.name, user.email)}
                            helperText={
                                incomplete(user.name, user.email)
                                    ? 'Both name and email are required'
                                    : ''
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            color="error"
                                            size="small"
                                            aria-label="Remove user"
                                            onClick={() =>
                                                onRemoveOtherUser(index)
                                            }
                                        >
                                            <DeleteIconSB fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </React.Fragment>
            ))}
        </>
    )
}
